import { FormControl, FormGroup } from "@angular/forms";
import { AssessmentPdfGenerator } from "src/app/patients/dashboard-page/assessments-history-tab/assessment-to-pdf";
import { ControlsOf, AssessmentHelpers } from "./shared";

interface IntegumentaryFields {
  /**
    * The appearance of the patient's skin.
    *
    * Options: diaphoresis, pallor, poor turgor, cool, modeling, and jaundice.
    */
  skinAppearance: string[];
  skinAppearanceComment: string;

  // Wounds

  /**
   * The location of the wound.
   */
  location: string;

  /**
   * The staging of the wound.
   *
   * Options: stage one, stage two, stage three, stage four, or unstageable.
   */
  staging: string;

  /**
   * The methods to care for the wound.
   *
   * Options: ABD, gauze, wet to dry dressing change, pharmaceutical agent (see MAR),
   * steri strips, staples, well approximated, and dehiscence.
   */
  care: string[];
}
export type IntegumentaryFormGroup = FormGroup<ControlsOf<IntegumentaryFields>>;

export const IntegumentaryAssessmentHelpers: AssessmentHelpers = {
  createFormGroup: createIntegumentaryFormGroup,
  toPDF: integumentaryAssessmentToPDF,
  formToObject: formToIntegumentaryAssessment,
  initialState: {
    skinAppearance: [[]],
    skinAppearanceComment: [''],
    // Wounds
    location: [''],
    staging: [''],
    care: [[]],
  },
};

/**
 * Holds information about the patient's integumentary assessment.
 */
export class IntegumentaryAssessment {
  skinAppearance: string[];
  skinAppearanceComment: string;
  // Wounds
  location: string;
  staging: string;
  care: string[];

  constructor(data: Partial<IntegumentaryFields>) {
    this.skinAppearance = data.skinAppearance || IntegumentaryAssessmentHelpers.initialState.skinAppearance;
    this.skinAppearanceComment = data.skinAppearanceComment || IntegumentaryAssessmentHelpers.initialState.skinAppearanceComment;

    // Wounds
    this.location = data.location || IntegumentaryAssessmentHelpers.initialState.location;
    this.staging = data.staging || IntegumentaryAssessmentHelpers.initialState.staging;
    this.care = data.care || IntegumentaryAssessmentHelpers.initialState.care;
  }
}

function createIntegumentaryFormGroup(integumentaryAssessment?: IntegumentaryAssessment) {
  const data = integumentaryAssessment || new IntegumentaryAssessment(IntegumentaryAssessmentHelpers.initialState);
  return new FormGroup<ControlsOf<IntegumentaryFields>>({
    skinAppearance: new FormControl<string[] | null>(data.skinAppearance),
    skinAppearanceComment: new FormControl<string | null>(data.skinAppearanceComment),
    // Wounds
    location: new FormControl<string | null>(data.location),
    staging: new FormControl<string | null>(data.staging),
    care: new FormControl<string[] | null>(data.care),
  });
}

function integumentaryAssessmentToPDF(pdf: AssessmentPdfGenerator, assessment: IntegumentaryAssessment): void {
  pdf.addSectionTitle("Integumentary");
  pdf.addOneColumnText("Skin Appearance", assessment.skinAppearance.join(", "));
  if (assessment.skinAppearanceComment) {
    pdf.addOneColumnText("Comment", assessment.skinAppearanceComment);
  }
  pdf.currY += 10;
  pdf.pdf.text("Wounds", pdf.margin, pdf.currY);
  pdf.currY += 20;
  pdf.addTwoColumnText("Location", assessment.location, "Staging", assessment.staging);
  pdf.addOneColumnText("Wound Care", assessment.care.join(", "));
  pdf.currY += pdf.newSection;

}

function formToIntegumentaryAssessment(fg: IntegumentaryFormGroup) {
  return new IntegumentaryAssessment({
    skinAppearance: fg.controls.skinAppearance.value!,
    skinAppearanceComment: fg.controls.skinAppearanceComment.value!,
    // Wounds
    location: fg.controls.location.value!,
    staging: fg.controls.staging.value!,
    care: fg.controls.care.value!,
  });
}

